import { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import produce from 'immer';
import { findDOMNode } from 'react-dom';
import { useHistory, useLocation } from 'react-router-dom';
import { NavMarker } from 'react-rhumb';
import { useSelector, useDispatch } from 'react-redux';
import globalNavHeight from 'nav-meta/global-nav-height';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { H3, H4 } from 'UIComponents/elements/headings';
import Big from 'UIComponents/elements/Big';
import UIBanner from 'UIComponents/alert/UIBanner';
import UIBreadcrumbs from 'UIComponents/nav/UIBreadcrumbs';
import UIButton from 'UIComponents/button/UIButton';
import UICardGrid from 'UIComponents/card/UICardGrid';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIPaginator from 'UIComponents/paginator/UIPaginator';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import UISection from 'UIComponents/section/UISection';
import * as ScreenSizes from 'ecosystem-marketplace-core/constants/layout/ScreenSizes';
import { isFulfilled, isUninitialized } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { getCmsFreeSignupUrl } from 'ecosystem-marketplace-core/assets/utils/urls';
/* @ts-expect-error module is untyped */
import { usePrevious } from 'ecosystem-marketplace-core/hooks/usePrevious';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { useScreenSize } from 'marketplace-ui-common/hooks/useScreenSize';
import MainSection from 'ecosystem-marketplace-core/components/layout/MainSection';
import MainSectionInner from 'ecosystem-marketplace-core/components/layout/MainSectionInner';
import MediaQuery from 'marketplace-ui-common/layout/components/MediaQuery';
/* @ts-expect-error module is untyped */
import ScrollToResults from 'ecosystem-marketplace-core/components/storefront/ScrollToResults';
// @ts-expect-error untyped dependency
import StorefrontResultsHeader from 'ecosystem-marketplace-core/components/storefront/StorefrontResultsHeader';

/* @ts-expect-error module is untyped */
import { updateResultsView } from '../../actions/StorefrontActions';
// @ts-expect-error module is untyped
import { useEmbedContext } from '../../hooks/useEmbedContext';
import { getOffsetFromPageNumber, getPageNumberFromOffset, getPaginatedPathname } from '../../utils/pagination';
import { PublicOnly } from '../../selectors/app';
// @ts-expect-error module is untyped
import { selectResultsView } from '../../selectors/storefront';
import { searchAssets } from '../../clients/ecosystemClient';
import { AssetSortDropdown } from '../../components/storefront/filters/AssetSortInput';
import { useStorefrontRouteParams } from '../hooks/useStorefrontRouteParams';
import { AssetFilterFields } from '../../components/storefront/filters/FilterFields';
import MobileFilterPanel from '../../components/storefront/filters/MobileFilterPanel';
import ResultsFilters from '../../components/storefront/resultsFilters/ResultsFilters';
import RouterAssetCard from '../RouterAssetCard';
import StorefrontDocumentHead from '../../components/storefront/documentHead/StorefrontDocumentHead';
import StorefrontSection from '../../components/storefront/StorefrontSection';
import StorefrontUsageTrackerContext from '../../context/StorefrontUsageTrackerContext';
import StorefrontViewToggle from '../../components/storefront/StorefrontViewToggle';
import ThemeCollectionsCardGrid from '../components/ThemeCollectionsCardGrid';
import * as AssetSort from '../../common/constants/AssetSort';
import { DEFAULT_ASSET_QUERY_LIMIT, DEFAULT_ASSET_SORT_VALUE } from '../../common/constants/AssetQuery/AssetQuery';
/* @ts-expect-error module is untyped */
import UsageViewTracker from '../../components/tracking/UsageViewTracker';
import { AssetResultsHeaderDescription } from '../../components/storefront/resultsHeader/ResultsHeaderDescription';
import { AssetResultsHeaderTitle } from '../../components/storefront/resultsHeader/ResultsHeaderTitle';
import { StorefrontPage } from '../StorefrontPage';
import BrowseBar from '../../components/storefront/browse/BrowseBar';
import { useOfferingType } from '../../context/OfferingTypeContext';
import { clearQueryFilters, clearOfferingQueryFilters, getComputedQuery, getLocationFromQuery, toFilterTags, toQueryString, toSearchQuery, toUsageEventProperties } from '../../common/utils/assetQuery';
import { getAllThemeCollectionsRoute, getTemplatesHomePageRoute } from '../../utils/urls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const NAV_BAR_HEIGHT_INT = parseInt(globalNavHeight, 10);
const getFullPathnameFromLocation = ({
  pathname,
  search
}) => {
  return `${pathname}${search}`;
};
const getPageviewKey = (pathname, searchTerm) => {
  return `pageview::${pathname}${searchTerm ? `?${searchTerm}` : ''}`;
};
function getFilterViewKey(pathname, query) {
  return `filterView::${pathname}?${toQueryString(query)}`;
}
const maybeGetI18nComponentText = component => {
  if (!(component instanceof FormattedMessage)) return null;
  const {
    message: componentMessage,
    options: componentOptions
  } = component.props;
  return unescapedText(componentMessage, componentOptions);
};
const EMPTY_SEARCH_RESPONSE = {
  items: [],
  total: 0
};

// We only want to send the tracking event once in total, not once per render.
let sentScrollToResultsTrackingEvent = false;
const AssetStorefrontContainer = ({
  collectionId,
  description,
  onFetchCollectionOfferingIds,
  title
}) => {
  const offeringType = useOfferingType();
  const history = useHistory();
  const routeParams = useStorefrontRouteParams(offeringType);
  const location = useLocation();
  const dispatch = useDispatch();
  const resultsView = useSelector(selectResultsView);
  const {
    pathname: currentPathname
  } = location;
  const prevPathname = usePrevious(currentPathname);
  const fullPathname = getFullPathnameFromLocation(location);
  const fullPathnameRef = useRef(fullPathname);
  const resultsFiltersRef = useRef(null);
  const {
    isEmbedded
  } = useEmbedContext();
  const isUnifiedSearch = /^\/templates\/search/.test(currentPathname);
  const defaultAssetSortValue = isUnifiedSearch ? AssetSort.RELEVANCE_DESC : DEFAULT_ASSET_SORT_VALUE;
  const {
    isMobileView
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.SM]: false
    }
  });
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const passedDescriptionText = useMemo(() => maybeGetI18nComponentText(description), [description]);
  const passedTitleText = useMemo(() => maybeGetI18nComponentText(title), [title]);
  const getBreadcrumbKey = useCallback(() => {
    if (isUnifiedSearch) {
      return 'marketplace-assets-ui.storefront.breadcrumbs.offeringType.ALL';
    }
    return `marketplace-assets-ui.storefront.breadcrumbs.offeringType.${offeringType}`;
  }, [isUnifiedSearch, offeringType]);

  /**
   * Asset query
   */
  const assetQueryUpdatedRef = useRef(false);
  const [assetQuery, setAssetQuery] = useState(getComputedQuery(offeringType, routeParams, location));
  const {
    limit,
    sortFields: sortFieldsValue,
    offeringTypes,
    offset: computedOffset,
    search: searchTerm
  } = assetQuery;
  const prevAssetQuery = usePrevious(assetQuery);
  const prevSearchTerm = usePrevious(searchTerm);

  /**
   * Asset results
   */
  const onUpdateResultsView = useCallback(value => {
    dispatch(updateResultsView(value));
  }, [dispatch]);
  const [requestState, setRequestState] = useState('UNINITIALIZED');
  const [searchResponse, setSearchResponse] = useState(EMPTY_SEARCH_RESPONSE);
  const {
    items: results,
    total
  } = searchResponse;
  const computedPageCount = useMemo(() => {
    return Math.ceil(total / limit);
  }, [limit, total]);
  const computedPageNumber = getPageNumberFromOffset(assetQuery.offset, DEFAULT_ASSET_QUERY_LIMIT);
  const computedResultsCount = useMemo(() => {
    if (!total) {
      return null;
    }
    const start = computedOffset + 1;
    return {
      start: total ? start : 0,
      end: computedOffset + Math.min(results.length, limit),
      total
    };
  }, [computedOffset, limit, results.length, total]);
  let renderedResultsCountMessage;
  if (searchTerm && offeringTypes.length) renderedResultsCountMessage = 'marketplace-assets-ui.templates.resultsHeader.searchResultsCount.filtered';else if (searchTerm) renderedResultsCountMessage = 'ecosystemMarketplaceCore.storefront.resultsHeader.searchResultsCount';else renderedResultsCountMessage = 'ecosystemMarketplaceCore.storefront.resultsHeader.resultsCount';

  /**
   * Usage tracking
   */
  const {
    trackEvent,
    trackInteraction,
    trackFilterView,
    trackPageview,
    setDefaultEventProperties
  } = useContext(StorefrontUsageTrackerContext);
  const getDefaultEventProperties = useCallback(() => {
    return Object.assign({}, toUsageEventProperties(assetQuery), {
      screen: 'storefront',
      type: offeringType === 'THEME' ? 'theme' : 'module',
      category: collectionId ? `collection::${collectionId}` : undefined,
      pageNumber: computedPageNumber,
      totalCount: total
    });
  }, [collectionId, computedPageNumber, assetQuery, total, offeringType]);

  // Set default event properties
  useEffect(() => {
    setDefaultEventProperties(getDefaultEventProperties());
  }, [getDefaultEventProperties, setDefaultEventProperties]);

  // Reset default event properties on unmount
  useEffect(() => () => setDefaultEventProperties(null), [setDefaultEventProperties]);

  // Cache & update keys to track page/filter views
  const [pageviewKey, setPageviewKey] = useState(getPageviewKey(currentPathname, searchTerm));
  useEffect(() => {
    setPageviewKey(getPageviewKey(currentPathname, searchTerm));
  }, [currentPathname, searchTerm]);
  const [filterViewKey, setFilterViewKey] = useState(null);
  useEffect(() => {
    if (currentPathname === prevPathname && searchTerm === prevSearchTerm && !assetQuery.equals(prevAssetQuery)) {
      setFilterViewKey(getFilterViewKey(currentPathname, assetQuery));
    }
  }, [currentPathname, prevPathname, prevSearchTerm, prevAssetQuery, searchTerm, assetQuery]);

  /**
   * Callback handlers
   */
  const handleAssetQueryChange = useCallback(value => {
    assetQueryUpdatedRef.current = true;
    setAssetQuery(value);
  }, []);
  const handlePageChange = useCallback(({
    target: {
      value
    }
  }) => {
    const newComputedOffset = getOffsetFromPageNumber(value, limit);
    const newQuery = produce(assetQuery, draft => {
      draft.offset = newComputedOffset;
    });
    handleAssetQueryChange(newQuery);
  }, [handleAssetQueryChange, limit, assetQuery]);
  const handleFilterUpdate = useCallback((field, value, shouldTrackEvent = true) => {
    handleAssetQueryChange(previousAssetQuery => {
      let updatedAssetQuery = produce(previousAssetQuery, draft => {
        Object.assign(draft, {
          [field]: value,
          offset: 0
        });
      });
      if (field === 'offeringTypes') {
        updatedAssetQuery = clearOfferingQueryFilters(updatedAssetQuery);
      }
      return updatedAssetQuery;
    });
    if (shouldTrackEvent) {
      let eventValue = value;
      if (value) {
        if (value.constructor.name === 'Object') {
          eventValue = Object.entries(value).map(([k, v]) => `${k}=${v}`);
        } else if (!Array.isArray(value)) {
          eventValue = [value];
        }
      }
      trackEvent('storefront-change-filter', {
        action: 'change filter',
        filter: field,
        value: eventValue
      });
    }
  }, [handleAssetQueryChange, trackEvent]);
  const handleApplyFilters = useCallback(filters => {
    setIsFilterPanelOpen(false);
    const updatedAssetQuery = produce(assetQuery, draft => {
      draft.offset = 0;
      return Object.assign(draft, filters);
    });
    handleAssetQueryChange(updatedAssetQuery);
    resultsFiltersRef.current.focus();
  }, [assetQuery, handleAssetQueryChange, setIsFilterPanelOpen]);
  const handleClearFilters = useCallback(() => {
    trackInteraction('clear all filters');
    const updatedQuery = clearQueryFilters(assetQuery);
    handleAssetQueryChange(updatedQuery);
  }, [handleAssetQueryChange, assetQuery, trackInteraction]);
  const handleClearFilterValue = useCallback((field, value) => {
    const queryValue = assetQuery[field];
    const newValue = Array.isArray(queryValue) ? queryValue.filter(v => v !== value) : null;
    handleFilterUpdate(field, newValue, false);
    let eventValue = value;
    if (value) {
      if (value.constructor.name === 'Object') {
        eventValue = Object.entries(value).map(([k, v]) => `${k}=${v}`);
      } else if (!Array.isArray(value)) {
        eventValue = [value];
      }
    }
    trackEvent('storefront-close-filter-tag', {
      action: 'close filter tag',
      filter: field,
      value: eventValue
    });
  }, [assetQuery, handleFilterUpdate, trackEvent]);
  const handleSortChange = useCallback(value => {
    handleFilterUpdate('sortFields', [value], false);
    trackInteraction('change sort', {
      sortField: value
    });
  }, [handleFilterUpdate, trackInteraction]);

  /**
   * Results scroller
   */
  const resultsRef = useRef(null);
  const [scrollToResultsOpacity, setScrollToResultsOpacity] = useState(0);
  const [scrollToResultsFixed, setScrollToResultsFixed] = useState(true);
  const detectResultsVisible = useCallback(() => {
    const resultsEl = findDOMNode(resultsRef.current);
    if (!(resultsEl instanceof Element)) return;
    if (!resultsEl.parentElement) return;
    setScrollToResultsFixed(resultsEl.parentElement.getBoundingClientRect().bottom > window.innerHeight);
    const rect = resultsEl.getBoundingClientRect();
    setScrollToResultsOpacity((NAV_BAR_HEIGHT_INT - rect.bottom) / 200);
  }, [resultsRef]);
  useEffect(() => {
    window.addEventListener('scroll', detectResultsVisible);
    return () => {
      window.removeEventListener('scroll', detectResultsVisible);
    };
  }, [detectResultsVisible]);
  useEffect(() => detectResultsVisible());
  useEffect(() => {
    if (requestState === 'SUCCEEDED' && computedPageCount > 0 && computedPageNumber > computedPageCount) {
      history.replace(getPaginatedPathname(currentPathname, 1));
    }
  }, [computedPageCount, computedPageNumber, history, currentPathname, requestState]);
  useEffect(() => {
    if (assetQueryUpdatedRef.current) {
      setSearchResponse(EMPTY_SEARCH_RESPONSE);
      setRequestState('UNINITIALIZED');
      const nextLocation = getLocationFromQuery(assetQuery, location, collectionId != null);
      history.push(nextLocation);
      fullPathnameRef.current = getFullPathnameFromLocation(nextLocation);
      assetQueryUpdatedRef.current = false;
    }
  }, [collectionId, history, location, assetQuery]);
  useEffect(() => {
    if (fullPathname !== fullPathnameRef.current) {
      setSearchResponse(EMPTY_SEARCH_RESPONSE);
      setRequestState('UNINITIALIZED');
      setAssetQuery(getComputedQuery(offeringType, routeParams, location));
      fullPathnameRef.current = getFullPathnameFromLocation(location);
    }
  }, [fullPathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isUninitialized(requestState)) {
      setRequestState('PENDING');
      let request;
      if (onFetchCollectionOfferingIds) {
        request = onFetchCollectionOfferingIds().then(offeringIds => {
          if (!offeringIds.length) {
            return Promise.resolve([{
              total: 0,
              items: []
            }]);
          }
          const updatedQuery = produce(assetQuery, draft => {
            draft.offeringIds = offeringIds;
          });
          const searchQuery = toSearchQuery(updatedQuery, offeringType, isUnifiedSearch);
          return searchAssets(searchQuery);
        });
      } else {
        const searchQuery = toSearchQuery(assetQuery, offeringType, isUnifiedSearch);
        request = searchAssets(searchQuery);
      }
      request.then(([resp]) => {
        setSearchResponse(resp);
        setRequestState('SUCCEEDED');
      }).catch(error => {
        console.error('error fetching assets\n', error);
        setSearchResponse(EMPTY_SEARCH_RESPONSE);
        setRequestState('FAILED');
      });
    }
  }, [assetQuery, isUnifiedSearch, offeringType, onFetchCollectionOfferingIds, requestState]);
  const renderedSearchResults = useMemo(() => {
    if (!results.length) {
      return /*#__PURE__*/_jsx(UIFlex, {
        direction: "column",
        children: searchTerm ? /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(H4, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-assets-ui.storefront.header.search.noResults",
              options: {
                searchTerm
              }
            })
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.storefront.header.search.noSearchResultsSubtext"
          })]
        }) : /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(H4, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-assets-ui.storefront.header.noResults"
            })
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.storefront.header.search.noFilterResultsSubtext"
          })]
        })
      });
    }
    return results.map((result, index) => /*#__PURE__*/_jsx(RouterAssetCard, {
      offeringType: result.offeringType || offeringType,
      onClick: () => {
        trackInteraction('click result item', {
          value: result.slug,
          itemIndex: index,
          totalResults: results.length
        });
      },
      onTrackInteraction: trackInteraction,
      onTrackPageview: trackPageview,
      result: result,
      view: resultsView
    }, `result-${index}`));
  }, [offeringType, results, searchTerm, resultsView, trackInteraction, trackPageview]);
  const renderedScrollToResults = useMemo(() => {
    const resultsEl = findDOMNode(resultsRef.current);
    if (!(resultsEl instanceof HTMLElement) || results.length === 0) return null;
    if (!sentScrollToResultsTrackingEvent && scrollToResultsOpacity > 0) {
      sentScrollToResultsTrackingEvent = true;
      trackInteraction('view scroll to results');
    }
    return /*#__PURE__*/_jsx(ScrollToResults, {
      containerWidth: resultsEl.offsetWidth,
      opacity: scrollToResultsOpacity,
      fixed: scrollToResultsFixed,
      onClick: () => trackInteraction('click scroll to results')
    });
  }, [results.length, scrollToResultsOpacity, scrollToResultsFixed, trackInteraction]);
  const renderedViewToggle = useMemo(() => /*#__PURE__*/_jsx(StorefrontViewToggle, {
    activeView: resultsView,
    onChange: onUpdateResultsView
  }), [resultsView, onUpdateResultsView]);
  const screenSize = useScreenSize();
  const isXSScreen = screenSize === 'xs';
  const gridColumns = isXSScreen ? 1 : 2;
  const columns = useMemo(() => resultsView === 'list' ? 1 : gridColumns, [resultsView, gridColumns]);
  const signupUrl = getCmsFreeSignupUrl();
  const trackBreadcrumbLinkClick = useCallback(value => {
    trackInteraction('click breadcrumb link', {
      value
    });
  }, [trackInteraction]);
  const sidebar = /*#__PURE__*/_jsx(MediaQuery, {
    minScreenSize: "lg",
    children: /*#__PURE__*/_jsxs(UIGridItem, {
      size: {
        lg: 3
      },
      children: [!isEmbedded && /*#__PURE__*/_jsxs(UIBreadcrumbs, {
        className: "p-bottom-5",
        flush: true,
        children: [/*#__PURE__*/_jsx(UIRouterLink, {
          onClick: () => trackBreadcrumbLinkClick('templates storefront home'),
          to: getTemplatesHomePageRoute(),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.storefront.breadcrumbs.root"
          })
        }), /*#__PURE__*/_jsx("span", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: getBreadcrumbKey()
          })
        })]
      }), /*#__PURE__*/_jsx(AssetFilterFields, {
        filters: assetQuery,
        onFilterChange: handleFilterUpdate
      })]
    })
  });
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "storefront-container",
    children: [requestState === 'SUCCEEDED' && /*#__PURE__*/_jsx(NavMarker, {
      name: "ASSETS_LOAD"
    }), /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(StorefrontDocumentHead, {
        description: passedDescriptionText,
        pageCount: computedPageCount,
        requestState: requestState,
        routeParams: routeParams,
        searchTerm: searchTerm,
        title: passedTitleText,
        total: total
      }), /*#__PURE__*/_jsx(StorefrontPage, {
        navigation: /*#__PURE__*/_jsx(BrowseBar, {
          hasActiveOfferingType: !isUnifiedSearch,
          previewCollectionId: collectionId
        }),
        sidebar: sidebar,
        children: /*#__PURE__*/_jsxs(UIGridItem, {
          size: {
            xs: 12,
            lg: 9
          },
          children: [/*#__PURE__*/_jsx(PublicOnly, {
            children: /*#__PURE__*/_jsx(UIBanner, {
              titleText: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "marketplace-assets-ui.storefront.signUpBanner.title"
              }),
              children: /*#__PURE__*/_jsxs(UIFlex, {
                align: "center",
                direction: isMobileView ? 'column' : 'row',
                justify: "start",
                children: [/*#__PURE__*/_jsx(FormattedMessage, {
                  message: "marketplace-assets-ui.storefront.signUpBanner.message"
                }), /*#__PURE__*/_jsx(UIButton, {
                  className: isMobileView ? '' : 'm-left-2',
                  "data-test-id": "storefront-signup-banner-button",
                  href: signupUrl,
                  onClick: () => trackInteraction('click cms sign up link'),
                  size: "extra-small",
                  style: {
                    display: 'block',
                    minWidth: '70px'
                  },
                  use: "primary-white",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "marketplace-assets-ui.storefront.signUpBanner.signUp"
                  })
                })]
              })
            })
          }), /*#__PURE__*/_jsx(StorefrontResultsHeader, {
            className: "m-top-6",
            description: /*#__PURE__*/_jsx(AssetResultsHeaderDescription, {
              description: description,
              routeParams: routeParams,
              searchTerm: searchTerm || undefined
            }),
            renderedResultsCount: computedResultsCount && /*#__PURE__*/_jsx(FormattedHTMLMessage, {
              message: renderedResultsCountMessage,
              options: {
                start: computedResultsCount.start,
                end: computedResultsCount.end,
                count: total,
                searchTerm,
                offeringType: offeringTypes.includes('THEME') ? unescapedText('marketplace-assets-ui.assets.filters.offeringType.themes') : unescapedText('marketplace-assets-ui.assets.filters.offeringType.modules')
              }
            }),
            renderedResultsFilters: /*#__PURE__*/_jsx(ResultsFilters, {
              filters: toFilterTags(assetQuery),
              onClearFilter: handleClearFilterValue,
              onClearAllFilters: handleClearFilters,
              onOpenFilterPanel: () => setIsFilterPanelOpen(true),
              resultsFiltersRef: resultsFiltersRef
            }),
            renderedSortDropdown: /*#__PURE__*/_jsx(MediaQuery, {
              minScreenSize: "lg",
              children: /*#__PURE__*/_jsx(AssetSortDropdown, {
                isUnifiedSearch: isUnifiedSearch,
                onSelect: value => handleSortChange(value),
                value: sortFieldsValue[0] || defaultAssetSortValue
              })
            }),
            resultsCount: computedResultsCount,
            routeParams: routeParams,
            searchTerm: searchTerm || undefined,
            title: /*#__PURE__*/_jsx(AssetResultsHeaderTitle, {
              routeParams: routeParams,
              searchTerm: searchTerm || undefined,
              title: title
            }),
            viewToggle: renderedViewToggle
          }), requestState === 'PENDING' && /*#__PURE__*/_jsx(UILoadingSpinner, {
            layout: "centered",
            minHeight: 150
          }), requestState === 'SUCCEEDED' && /*#__PURE__*/_jsxs(Fragment, {
            children: [/*#__PURE__*/_jsxs("div", {
              ref: resultsRef,
              "data-test-id": "storefront-results-container",
              children: [/*#__PURE__*/_jsx(UICardGrid, {
                gutterSize: 16,
                columns: columns,
                children: renderedSearchResults
              }), computedPageCount > 1 && /*#__PURE__*/_jsx(UIPaginator, {
                className: "m-top-8",
                onPageChange: handlePageChange,
                page: computedPageNumber,
                pageCount: computedPageCount
              })]
            }), renderedScrollToResults]
          })]
        })
      }), offeringType === 'THEME' && /*#__PURE__*/_jsx(MainSection, {
        color: "light",
        children: /*#__PURE__*/_jsx(MainSectionInner, {
          children: /*#__PURE__*/_jsx(StorefrontSection, {
            primaryGridSize: 9,
            subtitle: /*#__PURE__*/_jsx("p", {
              className: "m-bottom-0",
              children: /*#__PURE__*/_jsx(Big, {
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "marketplace-assets-ui.storefront.themes.themeCollectionsSubtitle"
                })
              })
            }),
            title: /*#__PURE__*/_jsx(H3, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "marketplace-assets-ui.storefront.themes.themeCollectionsTitle"
              })
            }),
            titleControls: /*#__PURE__*/_jsx(UIRouterLink, {
              onClick: () => trackInteraction('all collections link'),
              to: getAllThemeCollectionsRoute(),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "marketplace-assets-ui.storefront.themes.collectionsLink"
              })
            }),
            titleControlsMobile: "md",
            children: /*#__PURE__*/_jsx(UISection, {
              use: "island",
              children: /*#__PURE__*/_jsx(ThemeCollectionsCardGrid, {})
            })
          })
        })
      })]
    }), /*#__PURE__*/_jsx(UsageViewTracker, {
      active: isFulfilled(requestState),
      onTrackView: trackPageview
    }, pageviewKey), filterViewKey && /*#__PURE__*/_jsx(UsageViewTracker, {
      active: isFulfilled(requestState),
      onTrackView: trackFilterView
    }, filterViewKey), /*#__PURE__*/_jsx(MobileFilterPanel, {
      filters: assetQuery,
      isUnifiedSearch: isUnifiedSearch,
      offeringType: offeringType,
      onApplyFilters: handleApplyFilters,
      onClearFilters: handleClearFilters,
      onClose: () => {
        setIsFilterPanelOpen(false);
        resultsFiltersRef.current.focus();
      },
      open: isFilterPanelOpen
    })]
  });
};
export default AssetStorefrontContainer;